import React from 'react';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';

import CustomDateTimePicker from 'components/SharedFormComponents/CustomDateTimePicker';
import TimezonePicker from 'components/TimezonePicker';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';

import { useStore } from 'utils/hooks/store';
import { ScheduleFormValues } from 'utils/api/modules/schedules/schedules.types';
import { zoneCity, zoneLabel, zoneOffsetLabel } from 'utils/formatters/timezone';

import {
  SectionContainer,
  SectionHeader,
  FieldRow,
  FieldLabel,
  FieldHelper,
  RequiredIndicator,
  ZoneText,
} from '../../styles';
import FrequencyPicker from './FrequencyPicker';
import RecurrencePreview from './RecurrencePreview';
import { usePreviewCycles } from './usePreviewCycles';
import { FrequencyType, addWallClockMs, sameWallClockIn, wallClockGap } from '../../utils/rruleUtils';
import { scheduleZones } from '../../utils/zones';

interface TimingSectionProps {
  isNewSchedule?: boolean;
  zoneIsDefault?: boolean;
}

const TimingSection: React.FC<TimingSectionProps> = ({ isNewSchedule = false, zoneIsDefault = false }) => {
  const { values, initialValues, setFieldValue, errors, touched } = useFormikContext<ScheduleFormValues>();
  const { session } = useStore();
  const enableThreeYearRecurrence = session.user?.organisation.enableThreeYearRecurrence ?? false;
  const { displayZone, ruleZone, dayZone } = scheduleZones(
    values.timezone,
    zoneIsDefault,
    !!session.user?.timezoneModelEnabled
  );

  const preview = usePreviewCycles({
    frequency: values.frequency,
    startDate: values.startDate,
    dueDate: values.dueDate,
    zone: ruleZone,
    dayZone,
  });

  const isValidDate = (date: Date | null): date is Date => !!date && !Number.isNaN(date.getTime());

  const computeWindow = (startDate: Date | null, dueDate: Date | null): number | null =>
    isValidDate(startDate) && isValidDate(dueDate) ? wallClockGap(startDate, dueDate, ruleZone) : null;

  const windowRef = React.useRef<number | null>(computeWindow(values.startDate, values.dueDate));
  const prevInitialValuesRef = React.useRef(initialValues);
  if (prevInitialValuesRef.current !== initialValues) {
    prevInitialValuesRef.current = initialValues;
    windowRef.current = computeWindow(initialValues.startDate, initialValues.dueDate);
  }

  const FALLBACK_WINDOW_MS = 8 * 60 * 60 * 1000;

  const [dueFieldKey, setDueFieldKey] = React.useState(0);

  const inZoneTime = (value: Date | null): string =>
    displayZone ? ` In ${zoneCity(displayZone)} (${zoneOffsetLabel(displayZone, value ?? new Date())}) time.` : '';

  // Changing the zone keeps the times typed and moves the moment they mean: 09:00 in London becomes 09:00 in Chicago.
  const handleZoneChange = (next: string): void => {
    const current = values.timezone;
    if (current) {
      const rebase = (date: Date | null): Date | null =>
        isValidDate(date) ? sameWallClockIn(date, current, next) : date;
      setFieldValue('startDate', rebase(values.startDate));
      setFieldValue('dueDate', rebase(values.dueDate));
    }
    setFieldValue('timezone', next);
  };

  const handleStartDateChange = (value: AnswerTypes): void => {
    const date = typeof value === 'number' ? new Date(value) : null;
    setFieldValue('startDate', date);

    if (isValidDate(date) && isValidDate(values.dueDate) && date.getTime() >= values.dueDate.getTime()) {
      const gap = windowRef.current && windowRef.current > 0 ? windowRef.current : FALLBACK_WINDOW_MS;
      setFieldValue('dueDate', addWallClockMs(date, gap, ruleZone));
      setDueFieldKey((key) => key + 1);
    }
  };

  const handleDueDateChange = (value: AnswerTypes): void => {
    const date = typeof value === 'number' ? new Date(value) : null;
    setFieldValue('dueDate', date);

    if (isValidDate(date) && isValidDate(values.startDate)) {
      windowRef.current = wallClockGap(values.startDate, date, ruleZone);
    }
  };

  const handleFrequencyChange = (frequency: FrequencyType): void => {
    setFieldValue('frequency', frequency);
  };

  return (
    <SectionContainer>
      <SectionHeader>Timing</SectionHeader>

      {displayZone && (
        <FieldRow data-testid="schedule-timezone-field">
          <FieldLabel>
            Timezone
            {isNewSchedule && <RequiredIndicator>*</RequiredIndicator>}
          </FieldLabel>
          <FieldHelper data-testid="schedule-timezone-statement">
            Available From and Due By are in this timezone, wherever assignees are.
          </FieldHelper>
          {isNewSchedule ? (
            <TimezonePicker value={displayZone} onChange={handleZoneChange} ariaLabel="Schedule timezone" />
          ) : (
            <ZoneText>
              {zoneLabel(displayZone)}
              {zoneIsDefault && ', the account default'}
            </ZoneText>
          )}
        </FieldRow>
      )}

      <FieldRow data-testid="schedule-start-date-field">
        <FieldLabel>
          Available From
          <RequiredIndicator>*</RequiredIndicator>
        </FieldLabel>
        <FieldHelper>When assignees can start the audit.{inZoneTime(values.startDate)}</FieldHelper>
        <CustomDateTimePicker
          key={`start-${displayZone}`}
          size="medium"
          value={values.startDate ? values.startDate.getTime() : null}
          onChange={handleStartDateChange}
          requiredNotFilled={touched.startDate && !!errors.startDate}
          errorMessage={touched.startDate && errors.startDate ? String(errors.startDate) : undefined}
          description=""
          timezone={displayZone}
        />
      </FieldRow>

      <FieldRow data-testid="schedule-due-date-field">
        <FieldLabel>
          Due By
          <RequiredIndicator>*</RequiredIndicator>
        </FieldLabel>
        <FieldHelper>When assignees must finish.{inZoneTime(values.dueDate)}</FieldHelper>
        <CustomDateTimePicker
          key={`due-${displayZone}-${dueFieldKey}`}
          size="medium"
          value={values.dueDate ? values.dueDate.getTime() : null}
          onChange={handleDueDateChange}
          requiredNotFilled={!!values.dueDate && !!errors.dueDate}
          errorMessage={values.dueDate && errors.dueDate ? String(errors.dueDate) : undefined}
          description=""
          timezone={displayZone}
        />
      </FieldRow>

      <FieldRow>
        <FieldLabel>
          Frequency
          <RequiredIndicator>*</RequiredIndicator>
        </FieldLabel>
        <FrequencyPicker
          value={values.frequency}
          startDate={values.startDate}
          zone={dayZone}
          onChange={handleFrequencyChange}
          error={touched.frequency && !!errors.frequency}
          helperText={touched.frequency && errors.frequency ? String(errors.frequency) : undefined}
          enableThreeYearRecurrence={enableThreeYearRecurrence}
        />
        {!errors.startDate && !errors.dueDate && (
          <RecurrencePreview
            cycles={preview.cycles}
            isLoading={preview.isLoading}
            error={preview.error}
            zone={displayZone}
          />
        )}
      </FieldRow>
    </SectionContainer>
  );
};

export default observer(TimingSection);
